import { useCallback, useRef, useState } from 'react';
// import { ListingParams } from "../types/global";
import { getApiCallNew } from '../api/methods';
import { colorsClassNames, colorsNames } from '../Utils/constantData';
import { parseSpendingAmount } from '../Utils/helperFns';
// import { handleAPIError } from "../utils/toastify";
// import { convertToQueryParam } from "../utils/helperFunctions";

// The useListing is a custom React hook designed for managing paginated listing data from an API.
// It takes a URL, query parameters, and an optional path to extract specific data from the response.
// The hook provides functionality to fetch the listing data, update specific entries in the data, and reset the state.
// It handles loading and error states internally, using utility functions like convertToQueryParam and handleAPIError.
// The updateCurrentData function allows for updating individual items in the listing based on a given id and key-value pair.
// This hook is useful for managing dynamic data lists in applications.

interface ListingProps {
  url?: string;
  params?: any;
  path?: string;
  isInfinite?: boolean;
  isProduction?: boolean;
  isSet?: boolean;
  isProductionEpisodeSet?: boolean;
  isProductionEpisode?: boolean;
  isDepartment?: boolean;
  isDepartments?: boolean;
  isLocation?: boolean;
  setCountData?: any;
  isContact?: any;
  isStudio?: boolean;
  isCategory?: boolean;
  isPreferred?: boolean;
  isGroup?: boolean;
  isFilter?: boolean;
  isPieGraphData?: boolean;
  isDepartmentGraph?: boolean;
  isOrder?: boolean;
  isDropdownList?: boolean;
  isPopupProduction?: boolean;
  setProductions?: any;
  isPopupLocations?: boolean;
  setLocations?: any;
  isPopupSet?: boolean;
  setSets?: any;
  isPopupEpisode?: boolean;
  setEpisodes?: any;
  isPopupStudio?: boolean;
  setStudios?: any;
  isStudioProduction?: boolean;
  isOrders?: boolean;
  /** My Orders list: getApiCallNew puts the array in `data` and pagination in `fullData`. */
  isOrderList?: boolean;
  /** My Invoices list: same response shape as order list. */
  isInvoiceList?: boolean;
  isNotes?: boolean;
  isLocationDashboard?: boolean;
  isRiskDashboard?: boolean;
  isThreatList?: boolean;
  isChat?: boolean;
  isRawArrayResponse?: boolean;
  isDateFilterApplied?: boolean;
}

interface UpdateProps<T> {
  id: string;
  idKey?: keyof T;
  key: keyof T;
  value: T[keyof T];
}

const convertToQueryParam = (payload: Record<string, any>) => {
  const keys = Object.keys(payload);
  if (keys.length === 0) {
    return '';
  }
  const queryString = keys.reduce((query, key) => {
    // can get which type of formatting to do
    if (payload[key]) {
      return `${query}${key}=${encodeURIComponent(payload[key])}&`;
    }
    return query;
  }, '?');
  return queryString.substring(0, queryString.length - 1);
};

const pickOrderListSpending = (source: any) => ({
  totalSpending: parseSpendingAmount(source?.totalSpending),
  todaySpending: parseSpendingAmount(
    source?.todaySpending ?? source?.currentDateSpending,
  ),
});

/** Order/invoice list payloads vary: array at `data`, or `{ data: [], total }` after one unwrap. */
const parseOrderListResponse = (apiRes: any) => {
  const page = apiRes?.fullData;

  if (Array.isArray(apiRes?.data)) {
    return {
      rows: apiRes.data,
      total: page?.total ?? apiRes.data.length,
      ...pickOrderListSpending(page),
    };
  }

  if (Array.isArray(apiRes?.data?.data)) {
    return {
      rows: apiRes.data.data,
      total: apiRes.data.total ?? page?.total ?? 0,
      ...pickOrderListSpending(apiRes.data),
    };
  }

  if (Array.isArray(page?.data)) {
    return {
      rows: page.data,
      total: page.total ?? 0,
      ...pickOrderListSpending(page),
    };
  }

  if (Array.isArray(page?.data?.data)) {
    return {
      rows: page.data.data,
      total: page.data.total ?? page.total ?? 0,
      ...pickOrderListSpending(page.data),
    };
  }

  return { rows: [], total: 0, totalSpending: 0, todaySpending: 0 };
};

export type OrderListSummary = {
  totalSpending: number;
  todaySpending: number;
};

export const useListing = <T>({
  url,
  params,
  path,
  isInfinite,
  isProduction,
  isSet,
  isProductionEpisodeSet,
  isProductionEpisode,
  isDepartment,
  isDepartments,
  isLocation,
  setCountData,
  isContact,
  isStudio,
  isCategory,
  isPreferred,
  isGroup,
  isFilter,
  isPieGraphData,
  isDepartmentGraph,
  isOrder,
  isDropdownList,
  isPopupProduction,
  setProductions,
  isPopupLocations,
  setLocations,
  isPopupSet,
  setSets,
  isPopupEpisode,
  setEpisodes,
  isPopupStudio,
  setStudios,
  isStudioProduction,
  isOrders,
  isOrderList,
  isInvoiceList,
  isNotes,
  isLocationDashboard,
  isRiskDashboard,
  isThreatList,
  isChat,
  isRawArrayResponse,
  isDateFilterApplied,
}: ListingProps) => {
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [orderListSummary, setOrderListSummary] = useState<OrderListSummary>({
    totalSpending: 0,
    todaySpending: 0,
  });

  const dataRef = useRef<T[]>([]);
  const latestRequestIdRef = useRef(0);
  const isSummaryList = Boolean(isOrderList || isInvoiceList);

  const fetchListing = useCallback(async () => {
    if (!url) return;
    const requestId = ++latestRequestIdRef.current;

    setLoading(true);

    try {
      const queryParams: string = params ? convertToQueryParam(params) : '';
      // if (params?.searchKey || params?.search) {
      //   setData([]);
      // }

      const apiRes: any = await getApiCallNew(`${url}${queryParams}`);
      if (requestId !== latestRequestIdRef.current) return;
      const orderListParsed = isSummaryList
        ? parseOrderListResponse(apiRes)
        : null;
      const response = isSummaryList ? apiRes?.fullData : apiRes?.data;
      const success = apiRes?.success;

      if (success) {
        const dataToUpdate = isSummaryList
          ? orderListParsed?.rows || []
          : isRawArrayResponse && Array.isArray(response)
          ? response
          : isStudioProduction || isOrder || isDateFilterApplied
          ? response
          : isDepartmentGraph
          ? response
          : isPieGraphData
          ? [...response?.data]?.map((item: any, i: number) => ({
              ...item,
              className: colorsClassNames[i],
              color: colorsNames[i],
            })) || []
          : isFilter
          ? response?.data
          : isDropdownList
          ? response?.data
          : isContact
          ? response.userPermissions
          : isCategory
          ? response?.userDeptCategoryList
          : isGroup
          ? response?.data
          : isPreferred
          ? response?.myFavData
          : isDepartment
          ? response
          : isDepartments
          ? response?.depts
          : (!isSet && params?.entityType === 'studios') || isStudio
          ? response?.studios?.length
            ? response.studios
            : []
          : isPopupStudio
          ? response?.studios?.length
            ? response.studios
            : []
          : (!isSet && params?.entityType === 'productions') || isProduction
          ? response?.productions?.length
            ? response.productions
            : []
          : isPopupProduction
          ? response?.productions?.length
            ? response.productions
            : []
          : (!isSet && params?.entityType === 'sets') || isProductionEpisodeSet
          ? response?.sets?.length
            ? response.sets
            : []
          : isPopupSet
          ? response?.sets?.length
            ? response.sets
            : []
          : (!isSet && params?.entityType === 'episodes') || isProductionEpisode
          ? response?.episodes?.length
            ? response.episodes
            : []
          : isPopupEpisode
          ? response?.episodes?.length
            ? response.episodes
            : []
          : (!isSet && params?.entityType === 'locations') || isLocation
          ? response?.locations?.length
            ? response.locations
            : []
          : isPopupLocations
          ? response?.locations?.length
            ? response.locations
            : []
          : isNotes
          ? response?.notes?.data || []
          : isSet
          ? response?.data || response?.itemDetails || []
          : isOrders
          ? response
          : isLocationDashboard || isThreatList
          ? response.threats
          : isRiskDashboard
          ? response?.data
          : isChat
          ? response
          : [];

        if (setCountData) {
          setCountData(response);
        }

        const newData =
          params?.page === 1 || !isInfinite || params?.pageNo === 1
            ? isDropdownList
              ? [{ _id: 'All', name: 'All' }, ...dataToUpdate]
              : dataToUpdate || []
            : isDropdownList
            ? [...dataRef.current, ...dataToUpdate]
            : [...dataRef.current, ...dataToUpdate];

        setData(newData);

        if (isPopupProduction) {
          const newData =
            params?.page === 1 || !isInfinite || params?.pageNo === 1
              ? dataToUpdate
              : [...dataRef.current, ...dataToUpdate];
          setProductions(newData);
        }
        if (isPopupLocations) {
          const newData =
            params?.page === 1 || !isInfinite || params?.pageNo === 1
              ? dataToUpdate
              : [...dataRef.current, ...dataToUpdate];
          setLocations(newData);
        }
        if (isPopupSet) {
          const newData =
            params?.page === 1 || !isInfinite || params?.pageNo === 1
              ? dataToUpdate
              : [...dataRef.current, ...dataToUpdate];
          setSets(newData);
        }
        if (isPopupEpisode) {
          const newData =
            params?.page === 1 || !isInfinite || params?.pageNo === 1
              ? dataToUpdate
              : [...dataRef.current, ...dataToUpdate];
          setEpisodes(newData);
        }
        if (isPopupStudio) {
          const newData =
            params?.page === 1 || !isInfinite || params?.pageNo === 1
              ? dataToUpdate
              : [...dataRef.current, ...dataToUpdate];
          setStudios(newData);
        }

        dataRef.current = newData;

        const entityCount = isSummaryList
          ? orderListParsed?.total ?? 0
          : isOrder
          ? response?.total
          : isRawArrayResponse && Array.isArray(response)
          ? response.length
          : isStudioProduction
          ? response?.productionCount
          : isDepartmentGraph
          ? 0
          : isFilter
          ? response?.total
          : isDropdownList
          ? response?.total
          : isContact
          ? response?.userPermissionCount
          : (!isSet && params?.entityType === 'studios') || isStudio
          ? response?.studioCount
          : isPopupStudio
          ? response?.studioCount
          : isCategory
          ? response?.userDeptCategoryCount
          : isGroup
          ? response?.total
          : isPreferred
          ? response?.totalCount
          : isDepartments
          ? response.deptCount
          : (!isSet && params?.entityType === 'productions') || isProduction
          ? response?.productionCount
          : isPopupProduction
          ? response?.productionCount
          : (!isSet && params?.entityType === 'sets') || isProductionEpisodeSet
          ? response?.setCount
          : isPopupSet
          ? response?.setCount
          : (!isSet && params?.entityType === 'episodes') || isProductionEpisode
          ? response?.episodeCount
          : isPopupEpisode
          ? response?.episodeCount
          : (!isSet && params?.entityType === 'locations') || isLocation
          ? response?.locationCount
          : isPopupLocations
          ? response?.locationCount
          : isSet
          ? response?.total
          : isDepartment
          ? response?.length ||
            response?.episodeCount ||
            response?.productionCount ||
            response?.setCount ||
            response?.total // ||
          : isLocationDashboard || isThreatList
          ? response.total // response?.total
          : isRiskDashboard
          ? response.total
          : 0;

        setCount(entityCount);
        console.log('called!!');

        if (
          isSummaryList &&
          orderListParsed &&
          (params?.pageNo === 1 || params?.page === 1)
        ) {
          setOrderListSummary({
            totalSpending: orderListParsed.totalSpending,
            todaySpending: orderListParsed.todaySpending,
          });
        }

        if (params) {
          if (isSummaryList && orderListParsed) {
            const pageNo = params?.pageNo ?? 1;
            const pageLimit = params?.limit ?? 10;
            const pageRows = orderListParsed.rows ?? [];
            const total = orderListParsed.total ?? 0;
            setHasMore(pageRows.length > 0 && pageNo * pageLimit < total);
          } else {
            const pageLimit = params?.limit ?? 10;
            const currentPage = params?.page ?? params?.pageNo ?? 1;
            const batchSize = Array.isArray(dataToUpdate)
              ? dataToUpdate.length
              : 0;
            const hasMoreFromCount =
              entityCount > 0 && currentPage * pageLimit < entityCount;
            const hasMoreFromBatch =
              Boolean(isInfinite) && batchSize >= pageLimit;

            setHasMore(entityCount > 0 ? hasMoreFromCount : hasMoreFromBatch);
          }
        }

        setLoading(false);
      } else {
        if (isSummaryList) {
          setHasMore(false);
          if (params?.pageNo === 1 || params?.page === 1) {
            setData([]);
          }
        }
        console.log('rs', response);
        // handleAPIError(response);
      }
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;
      if (isSummaryList) {
        setHasMore(false);
      }
      console.log('error', error);
      //   handleAPIError(error);
    } finally {
      if (requestId !== latestRequestIdRef.current) return;
      setLoading(false);
    }
  }, [url, params, path]);

  const updateCurrentData = useCallback(
    ({ id, key, value, idKey = '_id' as keyof T }: UpdateProps<T>) => {
      console.log('dataresponse');
      const newData = dataRef.current.map(item => {
        if (item[idKey] === id) {
          return {
            ...item,
            [key]: value,
          };
        }
        return item;
      });

      setData(newData);
      dataRef.current = newData;
    },
    [],
  );

  const updateCurrentDataElement = useCallback((updatedElement: T[]) => {
    setData(updatedElement);
    dataRef.current = updatedElement;
  }, []);

  const reset = () => {
    // setLoading(false)
    dataRef.current = [];
    setData([]);
    setCount(0);
    setHasMore(false);
    setOrderListSummary({ totalSpending: 0, todaySpending: 0 });
  };

  return {
    fetchListing,
    data,
    count,
    loading,
    hasMore,
    orderListSummary,
    invoiceListSummary: orderListSummary,
    reset,
    updateCurrentData,
    updateCurrentDataElement,
    setData,
    setLoading,
  };
};
